"use client";

import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import {
  getWebPushVapidPublicKeyBase64,
  isWebPushSubscribeConfigured,
} from "@/lib/push-notifications/public-env";
import { ensurePushServiceWorkerRegistered } from "@/lib/push-notifications/ensure-push-service-worker";
import { urlBase64ToUint8Array } from "@/lib/push-notifications/vapid-client";
import { pushPermissionDeniedMessage } from "@/lib/notifications/push-settings-help";
import { recordNearMeFunnelMetric } from "@/lib/nearMeMetricsClient";

export type WebPushSubscribeSource =
  | "profile"
  | "setup_banner"
  | "setup_sheet"
  | "market"
  | "sell_success"
  | "pwa_post_install"
  | "onboarding";

export type WebPushHookState = {
  /** Browser supports Notification + Push + SW; secure context */
  isSupported: boolean;
  /** NEXT_PUBLIC_VAPID_PUBLIC_KEY is set */
  vapidConfigured: boolean;
  permission: NotificationPermission | "unsupported";
  /** Reflects an active PushSubscription in this browser */
  browserSubscribed: boolean;
  /** Push service endpoint for this browser, when subscribed */
  currentEndpoint: string | null;
  /** Saving to / removing from server */
  busy: boolean;
  lastError: string | null;
  enablePush: (options?: { source?: WebPushSubscribeSource }) => Promise<void>;
  disablePush: () => Promise<void>;
  refreshBrowserSubscription: () => Promise<void>;
};

function detectSupported(): boolean {
  if (typeof window === "undefined") {
    return false;
  }
  const secure =
    window.isSecureContext ||
    window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1";
  return (
    secure &&
    "Notification" in window &&
    "serviceWorker" in navigator &&
    "PushManager" in window
  );
}

async function getAccessToken(): Promise<string | null> {
  const { data } = await supabase.auth.getSession();
  return data.session?.access_token ?? null;
}

export function useWebPushSubscription(): WebPushHookState {
  const [permission, setPermission] = useState<
    NotificationPermission | "unsupported"
  >("unsupported");
  const [browserSubscribed, setBrowserSubscribed] = useState(false);
  const [currentEndpoint, setCurrentEndpoint] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [lastError, setLastError] = useState<string | null>(null);

  const isSupported = detectSupported();
  const vapidConfigured = isWebPushSubscribeConfigured();

  const refreshBrowserSubscription = useCallback(async () => {
    if (!detectSupported()) {
      setBrowserSubscribed(false);
      setCurrentEndpoint(null);
      return;
    }
    setPermission(Notification.permission);
    try {
      const reg = await ensurePushServiceWorkerRegistered();
      const sub = await reg.pushManager.getSubscription();
      setBrowserSubscribed(!!sub);
      setCurrentEndpoint(sub?.endpoint ?? null);
    } catch {
      setBrowserSubscribed(false);
      setCurrentEndpoint(null);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      void refreshBrowserSubscription();
    }, 0);
    return () => clearTimeout(timer);
  }, [refreshBrowserSubscription]);

  const enablePush = useCallback(
    async (options?: { source?: WebPushSubscribeSource }) => {
      setLastError(null);
      if (!isSupported || !vapidConfigured) {
        setLastError(
          "Push notifications are not available in this browser or the app is not configured.",
        );
        return;
      }

      const vapidB64 = getWebPushVapidPublicKeyBase64();
      if (!vapidB64) {
        setLastError("Missing VAPID public key configuration.");
        return;
      }

      if (Notification.permission === "denied") {
        setLastError(pushPermissionDeniedMessage());
        return;
      }

      setBusy(true);
      try {
        const perm = await Notification.requestPermission();
        setPermission(perm);
        if (perm !== "granted") {
          setLastError(
            perm === "denied"
              ? pushPermissionDeniedMessage()
              : "Permission was not granted.",
          );
          return;
        }

        const reg = await ensurePushServiceWorkerRegistered();
        const key = urlBase64ToUint8Array(vapidB64);
        let sub = await reg.pushManager.getSubscription();
        if (!sub) {
          sub = await reg.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: key as BufferSource,
          });
        }

        const token = await getAccessToken();
        if (!token) {
          setLastError("You must be signed in to enable push notifications.");
          return;
        }

        const json = sub.toJSON();
        const res = await fetch("/api/notifications/push/subscribe", {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            ...json,
            source: options?.source ?? "profile",
          }),
        });

        if (!res.ok) {
          const err = (await res.json().catch(() => ({}))) as {
            error?: string;
          };
          setLastError(err.error ?? `Request failed (${res.status})`);
          return;
        }

        setBrowserSubscribed(true);
        setCurrentEndpoint(sub.endpoint);
        void recordNearMeFunnelMetric("push_subscription_enabled", {
          source: options?.source ?? "profile",
        });
      } catch (e) {
        if (e instanceof DOMException && e.name === "NotAllowedError") {
          setLastError(pushPermissionDeniedMessage());
        } else {
          setLastError(
            e instanceof Error ? e.message : "Failed to enable push",
          );
        }
      } finally {
        setBusy(false);
      }
    },
    [isSupported, vapidConfigured],
  );

  const disablePush = useCallback(async () => {
    setLastError(null);
    if (!isSupported) {
      return;
    }

    setBusy(true);
    try {
      const reg = await ensurePushServiceWorkerRegistered();
      const sub = await reg.pushManager.getSubscription();
      const endpoint = sub?.endpoint;

      if (sub) {
        await sub.unsubscribe();
      }

      setBrowserSubscribed(false);
      setCurrentEndpoint(null);
      setPermission(
        "Notification" in window ? Notification.permission : "unsupported",
      );

      if (endpoint) {
        const token = await getAccessToken();
        if (token) {
          await fetch("/api/notifications/push/unsubscribe", {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ endpoint }),
          });
        }
      }
    } catch (e) {
      setLastError(e instanceof Error ? e.message : "Failed to disable push");
    } finally {
      setBusy(false);
    }
  }, [isSupported]);

  return {
    isSupported,
    vapidConfigured,
    permission,
    browserSubscribed,
    currentEndpoint,
    busy,
    lastError,
    enablePush,
    disablePush,
    refreshBrowserSubscription,
  };
}
